import { useEffect, useState } from 'react';
import { centsToInputValue, inputValueToCents } from '../../../lib/money';
import type { BudgetConfig } from '../../../types/metaAdDraft';
import { RadioGroup } from './RadioGroup';

// The amount field. Keeps the RAW text the user is typing in local state instead of
// reformatting `centsToInputValue(cents)` on every keystroke — reformatting "5" →
// "5.00" mid-type moved the caret to the end after each digit (cursor-jump bug).
// While focused we leave the raw text alone; the stored cents still update on every
// change (so the value is always correct). On blur — and whenever the canonical
// stored value changes externally while unfocused (budget-type switch, reset) — we
// re-sync the display to the formatted value.
function BudgetAmountInput({
  amountCents,
  onAmountCentsChange,
  placeholder,
  className,
}: {
  amountCents: number | null;
  onAmountCentsChange: (cents: number) => void;
  placeholder?: string;
  className?: string;
}) {
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState<string>(() => centsToInputValue(amountCents));

  useEffect(() => {
    if (!focused) setRaw(centsToInputValue(amountCents));
  }, [amountCents, focused]);

  return (
    <input
      type="text"
      inputMode="decimal"
      value={raw}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        setRaw(e.target.value);
        onAmountCentsChange(inputValueToCents(e.target.value) ?? 0);
      }}
      onBlur={() => {
        setFocused(false);
        setRaw(centsToInputValue(amountCents));
      }}
      placeholder={placeholder}
      className={className}
    />
  );
}

// Shared budget editor for CBO (campaign) + ABO (ad set). Amount is entered in
// the ad account's currency and stored as integer cents (kyck money rule).
export function BudgetFields({
  label,
  value,
  currency,
  timezoneName,
  onChange,
}: {
  label: string;
  value: BudgetConfig | null;
  currency: string | null;
  timezoneName: string | null;
  onChange: (budget: BudgetConfig) => void;
}) {
  const type = value?.type ?? 'daily';
  return (
    <div className="max-w-md space-y-3 rounded-md border border-slate-200 bg-slate-50/50 p-4">
      <div>
        <label className="mb-1.5 block text-sm font-medium text-slate-700">
          {label} {currency ? `(${currency})` : ''}
        </label>
        <BudgetAmountInput
          amountCents={value?.amountCents ?? null}
          onAmountCentsChange={(cents) => onChange({ amountCents: cents, type })}
          placeholder="50.00"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
        />
      </div>
      <RadioGroup
        label="Budget type"
        value={type}
        options={[
          { value: 'daily', label: 'Daily' },
          { value: 'lifetime', label: 'Lifetime' },
        ]}
        onChange={(t) => onChange({ amountCents: value?.amountCents ?? 0, type: t })}
      />
      {type === 'daily' && timezoneName && (
        <p className="text-xs text-slate-400">Daily budget resets at midnight {timezoneName}.</p>
      )}
    </div>
  );
}
